/**
 * Branded ID types for compile-time safety across the prefixed ID boundary.
 *
 * Per-entity types use template literals so the compiler enforces prefix matching:
 *   FlowId = `wf_${string}`   — only matches "wf_abc123", not "exec_abc123"
 *   FlowUuid = Uuid & { __entity: 'wf' } — a UUID known to reference a workflow
 *
 * @module types
 * @see packages/ids/src/prefixed-id.ts
 */

import { parseAnyId } from './prefixed-id.js';
import type { EntityPrefix } from './registry.js';

// ---------------------------------------------------------------------------
// Base branded types
// ---------------------------------------------------------------------------

/** A raw UUID. What the DB stores. What repos, services, and DB code accept. */
export type Uuid = string & { readonly __brand: 'Uuid' };

/** A prefixed entity ID like `wf_abc123`. What users see in URLs. */
export type EntityId = string & { readonly __brand: 'EntityId' };

/** Union: accepts both prefixed IDs and raw UUIDs (route params during migration). */
export type AnyEntityId = EntityId | Uuid;

// ---------------------------------------------------------------------------
// Per-entity prefixed ID types (template literal — compiler checks the prefix)
// ---------------------------------------------------------------------------

/** Derive a prefixed ID type from a prefix string: `wf_${string}` */
type PrefixedId<P extends string> = `${P}_${string}` & { readonly __brand: 'EntityId' };

export type OrganizationId = PrefixedId<typeof EntityPrefix.organization>;
export type FlowId = PrefixedId<typeof EntityPrefix.workflow>;
export type ExecutionId = PrefixedId<typeof EntityPrefix.workflowTestExecution>;
export type TestRunId = PrefixedId<typeof EntityPrefix.workflowTestRun>;
export type WorkflowRunId = PrefixedId<typeof EntityPrefix.workflowRun>;
export type IssueId = PrefixedId<typeof EntityPrefix.issue>;
export type PropertyId = PrefixedId<typeof EntityPrefix.property>;
export type CredentialId = PrefixedId<typeof EntityPrefix.credential>;
export type PerfTestId = PrefixedId<typeof EntityPrefix.perfTest>;
export type PerfRunId = PrefixedId<typeof EntityPrefix.perfRun>;

// ---------------------------------------------------------------------------
// Per-entity UUID types (branded — a UUID known to reference a specific entity)
// ---------------------------------------------------------------------------

/** A UUID branded with the entity type it references. */
type EntityUuid<P extends string> = Uuid & { readonly __entity: P };

export type OrganizationUuid = EntityUuid<typeof EntityPrefix.organization>;
export type FlowUuid = EntityUuid<typeof EntityPrefix.workflow>;
export type ExecutionUuid = EntityUuid<typeof EntityPrefix.workflowTestExecution>;
export type TestRunUuid = EntityUuid<typeof EntityPrefix.workflowTestRun>;
export type WorkflowRunUuid = EntityUuid<typeof EntityPrefix.workflowRun>;
export type IssueUuid = EntityUuid<typeof EntityPrefix.issue>;
export type PropertyUuid = EntityUuid<typeof EntityPrefix.property>;
export type CredentialUuid = EntityUuid<typeof EntityPrefix.credential>;
export type PerfTestUuid = EntityUuid<typeof EntityPrefix.perfTest>;
export type PerfRunUuid = EntityUuid<typeof EntityPrefix.perfRun>;

// ---------------------------------------------------------------------------
// Constructors
// ---------------------------------------------------------------------------

/** Brand a validated prefixed ID string. Returns null if not a valid prefixed format. */
export function toEntityId(value: string): EntityId | null {
  const parsed = parseAnyId(value);
  return parsed.kind === 'prefixed' ? (value as EntityId) : null;
}

/** Brand a known-valid UUID string. Use `extractUuid` for route params that may be prefixed. */
export function toUuid(value: string): Uuid | null {
  const parsed = parseAnyId(value);
  if (parsed.kind === 'raw_uuid') return parsed.uuid as Uuid;
  return null;
}

/** Brand any valid ID (prefixed or UUID). Returns null if invalid format. */
export function toAnyEntityId(value: string): AnyEntityId | null {
  const parsed = parseAnyId(value);
  return parsed.kind !== 'invalid' ? (value as AnyEntityId) : null;
}

// ---------------------------------------------------------------------------
// Pure extraction (the main function most routes use)
// ---------------------------------------------------------------------------

/**
 * Extract a UUID from any entity ID format (prefixed or raw UUID).
 *
 * Pure compute — no DB, no Effect, no org check. Returns a branded `Uuid`
 * that repos and services can accept. Returns null if unparseable.
 *
 * Most routes should use this. Only cross-org redirect endpoints need
 * the full `resolveEntityOrg` middleware.
 */
export function extractUuid(raw: string | null | undefined): Uuid | null {
  if (!raw) return null;
  const parsed = parseAnyId(raw);
  if (parsed.kind === 'prefixed') return parsed.uuid.toLowerCase() as Uuid;
  if (parsed.kind === 'raw_uuid') return parsed.uuid.toLowerCase() as Uuid;
  return null;
}
